import React from "react";

/**
 * HR Documents Table Skeleton Loader
 * Provides skeleton loading state for the HR Documents table during initial data fetch and page changes
 */
export const HrDocumentsTableSkeleton = ({ rows = 10, showDeleteColumn = false }) => {
  return (
    <>
      {Array.from({ length: rows }).map((_, index) => (
        <tr key={`hr-doc-skeleton-${index}`}>
          {/* Document Name */}
          <td style={{ paddingRight: "44px", maxWidth: "297px" }}>
            <div
              style={{
                width: `${180 + (index % 3) * 20}px`,
                height: "20px",
                backgroundColor: "#e5e7eb",
                borderRadius: "4px",
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            />
          </td>

          {/* Type */}
          <td style={{ paddingRight: "20px" }}>
            <div
              style={{
                width: `${80 + (index % 2) * 15}px`,
                height: "20px",
                backgroundColor: "#e5e7eb",
                borderRadius: "4px",
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            />
          </td>

          {/* Added by */}
          <td
            style={{
              paddingRight: "35px",
              maxWidth: "230px",
              minWidth: "230px",
              width: "230px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div
                style={{
                  width: "27px",
                  height: "27px",
                  backgroundColor: "#e5e7eb",
                  borderRadius: "50%",
                  animation: "pulse 1.5s ease-in-out infinite",
                  flexShrink: 0,
                }}
              />
              <div
                style={{
                  width: `${100 + (index % 3) * 10}px`,
                  height: "18px",
                  backgroundColor: "#e5e7eb",
                  borderRadius: "4px",
                  animation: "pulse 1.5s ease-in-out infinite",
                }}
              />
            </div>
          </td>

          {/* Date Added */}
          <td style={{ paddingRight: "20px" }}>
            <div
              style={{
                width: "150px",
                height: "20px",
                backgroundColor: "#e5e7eb",
                borderRadius: "4px",
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            />
          </td>

          {/* Download */}
          <td style={{ width: "2%", paddingRight: "20px" }}>
            <div
              style={{
                width: "20px",
                height: "20px",
                backgroundColor: "#e5e7eb",
                borderRadius: "4px",
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            />
          </td>

          {/* View */}
          <td style={{ width: "3%", paddingRight: "20px" }}>
            <div
              style={{
                width: "20px",
                height: "20px",
                backgroundColor: "#e5e7eb",
                borderRadius: "4px",
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            />
          </td>

          {/* Delete (conditional) */}
          {showDeleteColumn && (
            <td style={{ width: "3%", paddingRight: "20px", verticalAlign: "bottom" }}>
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  backgroundColor: "#e5e7eb",
                  borderRadius: "4px",
                  animation: "pulse 1.5s ease-in-out infinite",
                }}
              />
            </td>
          )}
        </tr>
      ))}
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </>
  );
};

/**
 * Manage Categories Link Skeleton Loader
 * Provides skeleton loading state for the "Manage Categories" link
 */
export const ManageCategoriesSkeleton = () => {
  return (
    <div className="manage manage-time-off">
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gridGap: "9px",
        }}
      >
        {/* Icon skeleton */}
        <div
          style={{
            width: "21px",
            height: "21px",
            backgroundColor: "#e5e7eb",
            borderRadius: "4px",
            animation: "pulse 1.5s ease-in-out infinite",
            flexShrink: 0,
          }}
        />
        {/* Text skeleton */}
        <div
          style={{
            width: "140px",
            height: "17px",
            backgroundColor: "#e5e7eb",
            borderRadius: "4px",
            animation: "pulse 1.5s ease-in-out infinite",
          }}
        />
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
};

/**
 * HR Documents Full Page Skeleton Loader
 * Provides skeleton loading state for the entire HR Documents page during lazy loading
 */
export const HrDocumentsPageSkeleton = () => {
  return (
    <div className="user-outer-overview invoice-outer-invoice invite-history document-hr-sec-outer">
      <div className="user-outer-overview add-bank-account">
        {/* Manage Categories skeleton */}
        <ManageCategoriesSkeleton />
        {/* Header section skeleton */}
        <div className="invoice-top" style={{ marginBottom: '20px' }}>
          <div className="button-sec-invoice upload-doc-hr">
            <div
              style={{
                width: "140px",
                height: "40px",
                backgroundColor: "#e5e7eb",
                borderRadius: "6px",
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            />
          </div>
          <div className="search-sec">
            <div className="search-container">
              <div
                style={{
                  width: "300px",
                  height: "40px",
                  backgroundColor: "#e5e7eb",
                  borderRadius: "6px",
                  animation: "pulse 1.5s ease-in-out infinite",
                }}
              />
            </div>
          </div>
        </div>

        {/* Filters section skeleton */}
        <div className="invoice-filter" style={{ marginBottom: '20px' }}>
          <div className="invoice-filter-inner-section no-flter-all hr-doc-filter">
            <div className="filter-inner account-type">
              <div
                style={{
                  width: "140px",
                  height: "16px",
                  backgroundColor: "#e5e7eb",
                  borderRadius: "4px",
                  animation: "pulse 1.5s ease-in-out infinite",
                  marginBottom: "8px",
                }}
              />
              <div
                style={{
                  width: "180px",
                  height: "40px",
                  backgroundColor: "#e5e7eb",
                  borderRadius: "6px",
                  animation: "pulse 1.5s ease-in-out infinite",
                }}
              />
            </div>
            <div className="filter-inner" style={{ marginLeft: "12px" }}>
              <div
                style={{
                  width: "100px",
                  height: "16px",
                  backgroundColor: "#e5e7eb",
                  borderRadius: "4px",
                  animation: "pulse 1.5s ease-in-out infinite",
                  marginBottom: "8px",
                }}
              />
              <div
                style={{
                  width: "200px",
                  height: "40px",
                  backgroundColor: "#e5e7eb",
                  borderRadius: "6px",
                  animation: "pulse 1.5s ease-in-out infinite",
                }}
              />
            </div>
            <div className="filter-inner" style={{ marginLeft: "12px" }}>
              <div
                style={{
                  width: "120px",
                  height: "16px",
                  backgroundColor: "#e5e7eb",
                  borderRadius: "4px",
                  animation: "pulse 1.5s ease-in-out infinite",
                  marginBottom: "8px",
                }}
              />
              <div
                style={{
                  width: "100px",
                  height: "40px",
                  backgroundColor: "#e5e7eb",
                  borderRadius: "6px",
                  animation: "pulse 1.5s ease-in-out infinite",
                }}
              />
            </div>
          </div>
        </div>

        {/* Table section skeleton */}
        <div className="card-outer1">
          <div className="card-outer card-outer-account">
            <div className="card">
              <div
                className="p-datatable p-component p-datatable-responsive-scroll"
                style={{
                  marginBottom: 0,
                  paddingBottom: 0,
                }}
              >
                <div
                  className="p-datatable-wrapper"
                  style={{
                    paddingBottom: 0,
                    marginBottom: 0,
                  }}
                >
                  <table
                    className="p-datatable-table"
                    style={{
                      minWidth: '50rem',
                      width: '100%',
                      textAlign: 'left',
                      marginBottom: 0,
                      borderSpacing: 0,
                    }}
                  >
                    <thead className="p-datatable-thead">
                      <tr>
                        <th style={{ paddingRight: '44px', maxWidth: '297px' }}>Document Name</th>
                        <th style={{ paddingRight: '20px' }}>Type</th>
                        <th
                          style={{
                            paddingRight: '35px',
                            maxWidth: '230px',
                            minWidth: '230px',
                            width: '230px',
                          }}
                        >
                          Added by
                        </th>
                        <th style={{ paddingRight: '20px' }}>Date Added</th>
                        <th style={{ width: '2%', paddingRight: '20px' }}></th>
                        <th style={{ width: '3%', paddingRight: '20px' }}></th>
                      </tr>
                    </thead>
                    <tbody className="p-datatable-tbody" style={{ marginBottom: 0, paddingBottom: 0 }}>
                      <HrDocumentsTableSkeleton rows={10} showDeleteColumn={false} />
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
};

/**
 * Modal Skeleton Loader
 * Provides skeleton loading state for modals during lazy loading
 */
export const ModalSkeleton = () => {
  return (
    <div
      style={{
        padding: '40px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px',
      }}
    >
      <div
        style={{
          width: "200px",
          height: "24px",
          backgroundColor: "#e5e7eb",
          borderRadius: "4px",
          animation: "pulse 1.5s ease-in-out infinite",
          marginBottom: "20px",
        }}
      />
      <div
        style={{
          width: "300px",
          height: "40px",
          backgroundColor: "#e5e7eb",
          borderRadius: "6px",
          animation: "pulse 1.5s ease-in-out infinite",
        }}
      />
      <style>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
};

export default HrDocumentsTableSkeleton;

